import { forwardRef, useEffect, useRef, type MutableRefObject, useImperativeHandle, useState, useMemo } from 'react';
import { FAIcon } from '../Icons/Icons';
import { LinkTarget, Theme } from '../types/enums';
import { TextLinkUnderlineStyle } from '../TextLink/TextLink';
import RawTextLink from '../TextLink/RawTextLink';
import { NextgenDOMPurify } from '../utils';
import type { LabelConfig, NotificationBarPropType, NotificationBarTheme } from './types/propTypes';
import styles from './NotificationBar.module.scss';

function isEllipsisActive(element: HTMLDivElement) {
  const { height, lineHeight } = getComputedStyle(element);
  const updatedLineHeight = Math.ceil(parseFloat(lineHeight));
  const updatedHeight = Math.ceil(parseFloat(height));
  return element.scrollWidth > element.clientWidth || updatedHeight > updatedLineHeight;
}

/**
 * @description
 * Notification Bar component used to display full with bar with blue color theme.
 * Figma:- https://www.figma.com/file/NmGzJoDRtkn481G3lgg0Z9/Amway-DS-–%C2%A0Global-Core-Components?t=rf95r7TuC5gW3g13-0
 * @param message
 * @type string
 * @description to display in notification bar.
 *
 * @param iconClickHandler
 * @type function
 * @description click handler for icon.
 * @returns Notification Bar React Node
 *
 * @param ctaLabel
 * @type string
 * @description take label to show for action.
 *
 * @param theme
 * @type string
 * @description background color
 *
 * @param ctaClickHandler
 * @type function
 * @description handle click event
 *
 */
const NotificationBar = forwardRef<HTMLDivElement, NotificationBarPropType>(
  (
    { message, theme = Theme.PURPLE, ctaLabel, ctaClickHandler, iconClickHandler, innerHtml }: NotificationBarPropType,
    ref,
  ) => {
    const notificationBarRef = useRef() as MutableRefObject<HTMLDivElement>;
    const viewMoreRef = useRef() as MutableRefObject<HTMLSpanElement>;
    const [actionLabel, setActionLabel] = useState<string>();

    const collapseCls = styles['notification-collapsed'];

    useImperativeHandle(ref, () => notificationBarRef.current);

    const checkEllipsis = () => {
      const isEllipsis = isEllipsisActive(notificationBarRef.current);
      if (viewMoreRef.current) {
        if (isEllipsis) {
          viewMoreRef.current.style.display = 'inline';
        } else {
          handleCTALabelSwitch();
          notificationBarRef.current.classList.add(collapseCls);
          viewMoreRef.current.style.display = 'none';
        }
      }
    };

    const handleCTALabelSwitch = (key: keyof LabelConfig = 'primaryLabel') => {
      if (typeof ctaLabel === 'object') {
        setActionLabel(ctaLabel[key]);
      } else {
        setActionLabel(ctaLabel);
      }
    };

    const ctaCB = () => {
      const { classList } = notificationBarRef.current;
      if (classList.contains(collapseCls)) {
        notificationBarRef.current.classList.remove(collapseCls);
        handleCTALabelSwitch('secondaryLabel');
      } else {
        notificationBarRef.current.classList.add(collapseCls);
        handleCTALabelSwitch();
      }
      ctaClickHandler?.();
    };

    useEffect(() => {
      handleCTALabelSwitch();
    }, []);

    useEffect(() => {
      if (notificationBarRef?.current) {
        checkEllipsis();
        window.addEventListener('resize', checkEllipsis);
        return () => window.removeEventListener('resize', checkEllipsis);
      }
    }, [notificationBarRef?.current]);

    const sanitizedHtml = useMemo(
      () => (innerHtml ? NextgenDOMPurify(innerHtml, { ADD_ATTR: ['target'] }) : null),
      [innerHtml],
    );
    return (
      <section className={`${styles['notificationbar-container']} ${styles[theme]}`}>
        {sanitizedHtml ? (
          <article
            ref={notificationBarRef}
            className={`${styles['notificationbar-body']} ${collapseCls}`}
            data-testid="notification-message-testid"
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
          />
        ) : (
          <article
            ref={notificationBarRef}
            className={`${styles['notificationbar-body']} ${collapseCls}`}
            data-testid="notification-message-testid"
          >
            {message}
          </article>
        )}
        {ctaLabel && (
          <span ref={viewMoreRef} className={styles['notificationbar-viewmore']} data-testid="viewmore-testid">
            <RawTextLink
              href=""
              text={actionLabel as string}
              theme={Theme.WHITE}
              onClick={ctaCB}
              underline={TextLinkUnderlineStyle.FIXED}
            />
          </span>
        )}
        {iconClickHandler && (
          <FAIcon
            iconName="xmark"
            theme={Theme.WHITE}
            className={styles['close-icon']}
            onClick={iconClickHandler}
            data-testid="notification-icon"
          />
        )}
      </section>
    );
  },
);
NotificationBar.displayName = 'NotificationBar';

export default NotificationBar;
export { LinkTarget };
export type { NotificationBarPropType, NotificationBarTheme };
